import { useEffect } from "react";
import { NavHeader } from "../components/NavHeader";
import { Footer } from "../components/Footer";
import { Disclaimer } from "../components/Disclaimer";
import {
  Briefcase, Heart, Home as HomeIcon, Calendar as CalIcon, Coins,
  Leaf, Scale, Star, Sparkles, MessageCircle, Lock
} from "lucide-react";

const CHAPTERS = [
  { Icon: Briefcase, label: "Career and calling", text: "What your work wants from you in the next year, where ambition gets rewarded, and the kind of role you should turn down even when it looks like a promotion." },
  { Icon: Heart,     label: "Love and romance",   text: "What you bring to a relationship, the pattern you keep repeating, and the kind of partner who lasts longer than the chemistry." },
  { Icon: HomeIcon,  label: "Family and home",    text: "The quiet politics of where you came from, what you owe and what you do not, and the season ahead at home." },
  { Icon: CalIcon,   label: "The year ahead",     text: "A four-quarter outlook, not a vague horoscope. What is likely in Q1, Q2, Q3, Q4, with timing where the chart allows it." },
  { Icon: Coins,     label: "Wealth and finance", text: "How money moves through your chart, where the leaks are, and the months that reward boldness over caution." },
  { Icon: Leaf,      label: "Health and vitality", text: "Your constitution's tilt, the habits your sign drifts toward under stress, and the simple corrections that hold up." },
  { Icon: Scale,     label: "Strengths and shadows", text: "The gifts you under-claim and the patterns you over-do. The honest mirror chapter, written without flattery." },
];

const ANCHORS = [
  { name: "Sun sign", desc: "Derived directly from your date of birth using the traditional tropical zodiac, the system in active use across Western astrology since Ptolemy. The most stable layer of you." },
  { name: "Element", desc: "Fire, earth, air or water. Sets your temperament: the speed at which you move, the way you grieve, the kind of risk that feels natural." },
  { name: "Modality", desc: "Cardinal, fixed or mutable. Sets your operating style: whether you start things, hold things, or change shape around things." },
  { name: "Ruling planet", desc: "Each sign is governed by a planet whose nature colours the sign. Mars for Aries, Venus for Taurus, Mercury for Gemini. The planet is the engine; the sign is the chassis." },
];

export const AboutPage = () => {
  useEffect(() => {
    document.title = "About · Inside the Astrarium almanac";
  }, []);
  return (
    <>
      <NavHeader />
      <main
        data-testid="about-page"
        className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 pt-28 sm:pt-32 pb-12"
      >
        <p className="text-stardust/70 uppercase tracking-[0.5em] text-[10px] mb-5 text-center">What&apos;s in a reading</p>
        <h1 className="font-display text-4xl sm:text-6xl text-[#F5EBD8] text-center leading-tight mb-5">
          An almanac, drawn for the moment you arrived<span className="text-stardust">.</span>
        </h1>
        <p className="font-serif-italic text-slate-300/90 text-lg sm:text-2xl leading-relaxed text-center mb-16 max-w-2xl mx-auto">
          Not a daily horoscope. Not a personality quiz. A long-form reading composed
          from the anchors of your birth chart and written for the specific shape of
          your life.
        </p>

        {/* The seven chapters */}
        <section className="mb-16" data-testid="about-chapters">
          <p className="text-stardust/70 uppercase tracking-[0.4em] text-[10px] mb-3">The seven chapters</p>
          <h2 className="font-display text-3xl sm:text-4xl text-[#F5EBD8] mb-6 leading-tight">
            Seven chapters, written tight, not padded.
          </h2>
          <p className="text-slate-300 leading-relaxed mb-8">
            A full reading runs across seven chapters, each rendered for the chart
            anchors of your specific birth. They get specific about timing where the
            chart allows it. They skip the four-adjective parade.
          </p>
          <div className="space-y-5">
            {CHAPTERS.map(({ Icon, label, text }) => (
              <article key={label} className="flex gap-4 sm:gap-5">
                <div className="shrink-0 h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-stardust/[0.08] border border-stardust/25 flex items-center justify-center">
                  <Icon className="h-4 w-4 sm:h-[18px] sm:w-[18px] text-stardust" />
                </div>
                <div>
                  <h3 className="font-display text-xl sm:text-2xl text-[#F5EBD8] mb-1.5 leading-tight">{label}</h3>
                  <p className="text-slate-300 text-sm sm:text-base leading-relaxed">{text}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Anchors */}
        <section className="mb-16" data-testid="about-anchors">
          <p className="text-stardust/70 uppercase tracking-[0.4em] text-[10px] mb-3">The chart anchors</p>
          <h2 className="font-display text-3xl sm:text-4xl text-[#F5EBD8] mb-6 leading-tight">
            Four things astrology reads first.
          </h2>
          <p className="text-slate-300 leading-relaxed mb-8">
            Before language, there is structure. Every Astrarium reading is anchored
            to four classical reference points that have organised astrological
            thought for centuries.
          </p>
          <dl className="space-y-6">
            {ANCHORS.map((a) => (
              <div key={a.name} className="border-l-2 border-stardust/30 pl-5">
                <dt className="font-display text-xl text-[#F5EBD8] mb-1.5">{a.name}</dt>
                <dd className="text-slate-300 text-sm sm:text-base leading-relaxed">{a.desc}</dd>
              </div>
            ))}
          </dl>
          <p className="text-slate-400 text-sm sm:text-base leading-relaxed mt-8 italic">
            If you share your time of birth, the reading also gestures at your rising
            sign, the constellation that was climbing the horizon at the minute you
            were born. It changes every two hours and shapes how the rest of the
            world reads you.
          </p>
        </section>

        {/* Lucky forecast */}
        <section className="mb-16" data-testid="about-lucky">
          <p className="text-stardust/70 uppercase tracking-[0.4em] text-[10px] mb-3">A favourable forecast</p>
          <h2 className="font-display text-3xl sm:text-4xl text-[#F5EBD8] mb-6 leading-tight">
            Six anchors of fortune.
          </h2>
          <p className="text-slate-300 leading-relaxed mb-4">
            Each almanac closes with a small forecast of favourable signs: a day, a
            colour, a number, a gemstone, a compass direction, and the signs you tend
            to harmonise with. These are not predictions of luck. They are practical
            anchors drawn from traditional planetary rulerships, the kind of small
            daily ritual our grandparents would have recognised.
          </p>
        </section>

        {/* Ask the wheel */}
        <section className="mb-16" data-testid="about-ask">
          <div className="flex items-center gap-3 mb-3">
            <MessageCircle className="h-4 w-4 text-stardust" />
            <p className="text-stardust/70 uppercase tracking-[0.4em] text-[10px]">Ask the wheel</p>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl text-[#F5EBD8] mb-6 leading-tight">
            A real answer to a real question.
          </h2>
          <p className="text-slate-300 leading-relaxed">
            Beyond the almanac, you can ask Astrarium anything you want answered
            through your chart. Love decisions, career timing, family choices, big
            moves and small ones. Answers are short, specific, and rooted in the
            same anchors as the almanac. No essays. No horoscope-shaped non-answers.
          </p>
        </section>

        {/* How readings are composed */}
        <section className="mb-16" data-testid="about-composition">
          <div className="flex items-center gap-3 mb-3">
            <Sparkles className="h-4 w-4 text-stardust" />
            <p className="text-stardust/70 uppercase tracking-[0.4em] text-[10px]">How a reading is composed</p>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl text-[#F5EBD8] mb-6 leading-tight">
            Tradition first. Language second.
          </h2>
          <p className="text-slate-300 leading-relaxed mb-4">
            Astrarium is built on two layers. The first is structural. Your sun sign,
            element, modality and ruling planet are computed from your date of birth
            using the same tropical-zodiac date ranges that working astrologers have
            agreed on for centuries. There is nothing modern about that part.
          </p>
          <p className="text-slate-300 leading-relaxed mb-4">
            The second layer is interpretive. A modern language model, trained on a
            broad body of astrological, mythological and psychological writing, is
            used to translate those anchors into prose that is specific to the date
            and place you entered. We use it for one thing only: as a translator from
            symbol to sentence.
          </p>
          <p className="text-slate-300 leading-relaxed">
            The framework underneath is traditional astrology. The model is just the
            tongue it speaks in.
          </p>
        </section>

        {/* Privacy */}
        <section className="mb-16" data-testid="about-privacy">
          <div className="flex items-center gap-3 mb-3">
            <Lock className="h-4 w-4 text-stardust" />
            <p className="text-stardust/70 uppercase tracking-[0.4em] text-[10px]">Privacy and sharing</p>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl text-[#F5EBD8] mb-6 leading-tight">
            Free, private, and quietly built.
          </h2>
          <p className="text-slate-300 leading-relaxed">
            You do not need an account. Readings you do not share live only in your
            browser. If you choose to share, we mint an unguessable link that lives
            for seven days and then deletes itself from our records. We do not sell
            data. We do not run trackers. The full privacy notice lives on the legal
            page if you want to read it.
          </p>
        </section>

        {/* Not */}
        <section className="mb-4" data-testid="about-not">
          <p className="text-stardust/70 uppercase tracking-[0.4em] text-[10px] mb-3">What this isn&apos;t</p>
          <h2 className="font-display text-3xl sm:text-4xl text-[#F5EBD8] mb-6 leading-tight">
            A thoughtful prompt, not a prophecy.
          </h2>
          <p className="text-slate-300 leading-relaxed">
            An Astrarium reading is something to think with, not something to obey.
            We do not claim certainty about your future. We do not sell predictions
            as fact. Astrology is a symbolic language, a quiet old one, and we use it
            the way a careful astrologer would: to give you something specific enough
            to actually use.
          </p>
        </section>

        <Disclaimer />
      </main>
      <Footer />
    </>
  );
};
